import { useEffect, useRef, useState } from 'react'
import { useStripe } from '@stripe/react-stripe-js'
import { useStores } from './useStores'
import { SubscriptionDTO } from '../../../api-models'
import { toast } from 'react-toastify'
import { ProductAccounts } from '../services/user'
import { setDelay } from '../utils/timeouts'

export const useSubscriptionManagement = ({
	subscription,
	shouldLoad,
	onSuccess
}: {
	subscription: SubscriptionDTO | null | undefined
	shouldLoad?: boolean
	onSuccess?: VoidFunction
}): SubscriptionDTO | null | undefined => {
	const stripe = useStripe()
	const { accountStore, billingStore } = useStores()
	const { account } = accountStore

	const [updatedSubscription, setUpdatedSubscription] = useState<
		SubscriptionDTO | null | undefined
	>(subscription)
	const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined)

	const onConfirmPayment = async () => {
		const account = accountStore.account
		if (!account || !stripe || !updatedSubscription) return
		const piid = updatedSubscription?.pending_payment_intent
		if (!piid) return
		const pi = await billingStore.getPaymentIntent(account.id, piid)
		const pm = account.billing?.payment_method?.stripe_id
		await stripe.confirmCardPayment(pi.client_secret, { payment_method: pm })
		await setDelay(5000)
		await accountStore.getSubscriptionByAccountId(account.id)
		await accountStore.setAccountForId(account.id)
	}

	async function reFetchSubscription() {
		const sub =
			account && (await ProductAccounts.getSubscriptionByAccountId(account.id))
		sub && setUpdatedSubscription(sub)
	}

	useEffect(() => {
		if (updatedSubscription && shouldLoad) {
			if (updatedSubscription?.pending_payment_intent) {
				onSuccess && onSuccess()
				if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
				onConfirmPayment()
			} else if (updatedSubscription?.payment_failed_reason) {
				;(async () => {
					if (account) {
						await ProductAccounts.clearSubscriptionFailedMessage(account.id)
						await accountStore.getSubscriptionByAccountId(account.id)
					}
				})()
				toast.error(
					updatedSubscription.payment_failed_reason ||
						'Your subscription has been cancelled'
				)
				if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
				onSuccess && onSuccess()
			} else if (updatedSubscription?.status === 'ACTIVE') {
				;(async () => {
					account && (await accountStore.getSubscriptionByAccountId(account.id))
				})()
				onSuccess && onSuccess()
				if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
			}
		}
	}, [updatedSubscription, shouldLoad])

	useEffect(() => {
		if (
			!shouldLoad ||
			updatedSubscription?.status !== 'PENDING' ||
			updatedSubscription?.pending_payment_intent
		) {
			if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
		}
		if (
			updatedSubscription?.status === 'PENDING' &&
			shouldLoad &&
			!updatedSubscription?.pending_payment_intent
		) {
			if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
			intervalRef.current = setInterval(reFetchSubscription, 5000)
		}
		return () => {
			if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
		}
	}, [updatedSubscription, shouldLoad])

	useEffect(() => {
		if (subscription?.status === 'PENDING') setUpdatedSubscription(subscription)
	}, [subscription])

	useEffect(() => {
		if (!shouldLoad) {
			if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
		}
		return () => {
			if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = undefined }
		}
	}, [shouldLoad])

	return subscription
}
